import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../axios/axiosinstance';

// axiosInstance sets a default `Content-Type: application/json` header, which
// (unlike a plain browser `fetch`) axios does NOT auto-clear for FormData —
// so without this override the request goes out as "application/json" with a
// multipart body and no boundary, and Django silently sees an empty
// request.data/request.FILES (confirmed via a live-browser repro: raw fetch
// worked, axios with the default header didn't).
const MULTIPART_CONFIG = { headers: { 'Content-Type': undefined } };

// ---------- Employee: "Ask HR" ----------

export const useMyTickets = (options = {}) =>
  useQuery({
    queryKey: ['support-my-tickets'],
    queryFn: () => axiosInstance.get('api/support/tickets/').then((r) => r.data),
    ...options,
  });

export const useHrOptions = (options = {}) =>
  useQuery({
    queryKey: ['support-hr-options'],
    queryFn: () => axiosInstance.get('api/support/hr-options/').then((r) => r.data?.hrs || []),
    ...options,
  });

export const useCreateTicket = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ category, message, file, selectedHrId }) => {
      if (file) {
        const fd = new FormData();
        fd.append('category', category);
        fd.append('message', message || '');
        fd.append('selected_hr_id', selectedHrId);
        fd.append('attachment', file);
        return axiosInstance.post('api/support/tickets/', fd, MULTIPART_CONFIG).then((r) => r.data);
      }
      return axiosInstance.post('api/support/tickets/', { category, message, selected_hr_id: selectedHrId }).then((r) => r.data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support-my-tickets'] }),
  });
};

// ---------- Shared thread (employee owner or HR/admin) ----------

export const useTicketThread = (ticketId, options = {}) =>
  useQuery({
    queryKey: ['support-ticket', ticketId],
    queryFn: () => axiosInstance.get(`api/support/tickets/${ticketId}/`).then((r) => r.data),
    enabled: !!ticketId,
    ...options,
  });

// Explicit "I actually opened this thread" signal — advances the caller's
// seen-watermark server-side. Deliberately NOT called from the polling
// refetch in useTicketThread, only once when a thread is opened, so a
// background poll can never silently mark unseen messages as seen.
export const useMarkTicketRead = (ticketId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => axiosInstance.post(`api/support/tickets/${ticketId}/mark-read/`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['support-my-tickets'] });
      qc.invalidateQueries({ queryKey: ['support-admin-tickets'] });
    },
  });
};

export const usePostTicketMessage = (ticketId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ message, file }) => {
      if (file) {
        const fd = new FormData();
        fd.append('message', message || '');
        fd.append('attachment', file);
        return axiosInstance.post(`api/support/tickets/${ticketId}/messages/`, fd, MULTIPART_CONFIG).then((r) => r.data);
      }
      return axiosInstance.post(`api/support/tickets/${ticketId}/messages/`, { message }).then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
      qc.invalidateQueries({ queryKey: ['support-my-tickets'] });
      qc.invalidateQueries({ queryKey: ['support-admin-tickets'] });
    },
  });
};

// ---------- HR: Support Inbox ----------

// The API scopes this list to the HR identity in the verified JWT. Do not add
// a client-side assignee filter: it would expose tickets before filtering.
export const useAdminTickets = (filters = {}, options = {}) =>
  useQuery({
    queryKey: ['support-admin-tickets', filters],
    queryFn: () => axiosInstance.get('api/support/admin/tickets/', { params: filters }).then((r) => r.data),
    ...options,
  });

export const useSetTicketStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, status }) =>
      axiosInstance.patch(`api/support/admin/tickets/${ticketId}/status/`, { status }).then((r) => r.data),
    onSuccess: (_, { ticketId }) => {
      qc.invalidateQueries({ queryKey: ['support-admin-tickets'] });
      qc.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
    },
  });
};
